import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Grid, Dialog, DialogTitle, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MessageBox } from 'components/MessageBox/MessageBox';
import { InventoryItemModal } from 'components/InventoryItemModal';
import useHttp from 'hooks/useHttp';
import { scrollIntoView } from 'utils/scroll';
import { isLoading } from 'utils/misc';
import { DEFAULT_INVENTORY_ITEM_URL } from 'api/inventoryItem';
import { HTTP_METHODS } from 'constants';

const TITLE = 'Reactivate inventory item';

export const ReactivateInventoryItemModal = ({ open, onClose, onSuccess, itemId }) => {
    const topElementRef = useRef();
    const {
        data: dataReactivate,
        error: errorReactivate,
        clearData: clearDataReactivate,
        sendRequest: reactivateInventoryItem,
        loading: loadingReactivate,
    } = useHttp();
    const {
        data: inventoryItemData,
        error: errorGetInventoryItem,
        sendRequest: getInventoryItemData,
        loading: loadingData,
    } = useHttp();
    const [messageType, setMessageType] = useState('none');
    const [responseMessage, setResponseMessage] = useState('');
    const [inventoryItem, setInventoryItem] = useState(null);
    const [isInventoryItemModalOpen, setIsInventoryItemModalOpen] = useState(false);

    useEffect(() => {
        if (itemId) {
            (async () => {
                await getInventoryItemData({
                    url: `${DEFAULT_INVENTORY_ITEM_URL}/${itemId}`,
                });
            })();
        }
    }, [itemId]);

    useEffect(() => {
        if (errorGetInventoryItem !== null) {
            setResponseMessage(errorGetInventoryItem.response?.data?.message || 'An error occurred');
            setMessageType('error');
        }
    }, [errorGetInventoryItem]);

    useEffect(() => {
        if (inventoryItemData) {
            setInventoryItem(inventoryItemData);
        }
    }, [inventoryItemData]);

    useEffect(() => {
        clearDataReactivate();

        if (errorReactivate !== null) {
            setResponseMessage(errorReactivate.response?.data?.message || 'An error occurred');
            setMessageType('error');
        }

        if (dataReactivate) {
            onSuccess('Inventory item reactivated successfully.', 'success');
            onClose();
        }
    }, [errorReactivate, dataReactivate, messageType]);

    const handleEditConfirmation = () => {
        setIsInventoryItemModalOpen(true);
    };

    const handleInventoryItemModalClose = () => {
        setIsInventoryItemModalOpen(false);
        onClose();
    };

    const handleReactivateConfirmation = async () => {
        const requestBody = {
            ...inventoryItem,
            status: 'Available'
        };

        if (itemId) {
            await reactivateInventoryItem({
                url: `${DEFAULT_INVENTORY_ITEM_URL}/${itemId}`,
                method: HTTP_METHODS.PUT,
                data: requestBody,
            });
        }
        scrollIntoView(topElementRef);
    };

    const handleUpdateSuccess = () => {
        onSuccess('Inventory item updated successfully.', 'success');
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle style={{ textTransform: 'none' }}>
                {TITLE}
                <Button onClick={onClose} variant="CloseButton">
                    <CloseIcon />
                </Button>
            </DialogTitle>
            <DialogContent>
                <Grid width={500} ref={topElementRef}>

                    {isInventoryItemModalOpen ? (
                        <InventoryItemModal
                            open={isInventoryItemModalOpen}
                            onClose={handleInventoryItemModalClose}
                            onSuccess={handleUpdateSuccess}
                            itemId={itemId}
                        />
                    ) : (
                        <>
                            {messageType !== 'none' && (
                                <MessageBox messageType={messageType}>{responseMessage}</MessageBox>
                            )}
                            {messageType !== 'success' && (
                                <Grid item xs={12}>
                                    <Typography>
                                        Are you sure you want to reactivate the inventory item?
                                    </Typography>
                                    <Box mt={2} display="flex" justifyContent="flex-end">
                                        <Button
                                            onClick={onClose}
                                            variant="outlined"
                                            sx={{ marginRight: '8px' }}
                                            disabled={isLoading(loadingData, loadingReactivate)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleReactivateConfirmation}
                                            variant="contained"
                                            color="error"
                                            disabled={isLoading(loadingData, loadingReactivate)}
                                        >
                                            Reactivate
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                            {messageType === 'success' && (
                                <Grid item xs={12}>
                                    <Typography>Do you want to edit inventory item?</Typography>
                                    <Box mt={2} display="flex" justifyContent="flex-end">
                                        <Button
                                            onClick={onClose}
                                            variant="outlined"
                                            sx={{ marginRight: '8px' }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button onClick={handleEditConfirmation} variant="contained">
                                            Edit
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

ReactivateInventoryItemModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    itemId: PropTypes.string.isRequired
};
